import { useRef } from 'react';
import styles from './LoginScreen.module.css';
import TopoBackground from './TopoBackground';

/**
 * The screen for a sign-in that asked for one account and got another.
 *
 * Rendered instead of the app when the address typed at the email step is not the address
 * in the session that came back. Two things produce that, and both used to be silent. The
 * identity provider answers an authorize request from its own cookie, so on the MIKE Cloud
 * path nothing we send can make it ask who you are; and where our own tokens are still
 * alive in the service worker, pressing Continue never reaches an identity provider at all
 * and the existing session simply carries on. Either way somebody typed one address and
 * was signed in as another, with nothing on screen to give it away.
 *
 * Advisory rather than a block, and that is a deliberate trade. The comparison is between
 * an address a person typed and the claim their token carries, and those differ for
 * perfectly good reasons: a colleague may type the full-name alias that reaches their
 * mailbox while Entra mints the initials-based `preferred_username` the account is keyed
 * on. Refusing entry on that would lock out a legitimate login; naming the account and
 * offering the way out costs one click and is wrong in nobody's favour.
 *
 * Same palette-locked styles as the sign-in screens, since it is the same kind of page:
 * no chrome around it, seen outside the app.
 */
export default function AccountMismatchScreen({
  signedInAs,
  requested,
  onContinue,
  onSignOut,
}: {
  /** The address in the session that came back. */
  signedInAs: string;
  /** The address that was typed at the email step. */
  requested: string;
  onContinue: () => void;
  onSignOut: () => void;
}) {
  const viewRef = useRef<HTMLDivElement | null>(null);
  return (
    <div className={styles.view} ref={viewRef}>
      <img className={styles.logo} src={`${import.meta.env.BASE_URL}dhi-logo.png`} alt="DHI" />
      <TopoBackground containerRef={viewRef} />

      <div className={styles.content}>
        <h1 className={styles.title}>
          Docs<em className={styles.titleAccent}> AI</em>
        </h1>
        {/* The session first, as a fact, which is how Google and Microsoft word their own
            version of this screen. Leading with the address that was typed made the same
            sentence read as an accusation, and made the browser the subject of it. */}
        <p className={styles.subtitle} role="status">
          You&apos;re already signed in as {signedInAs}. To use {requested} instead, sign out first.
        </p>

        <button type="button" className={styles.button} onClick={onContinue}>
          Continue as {signedInAs}
        </button>

        <button type="button" className={styles.inlineAction} onClick={onSignOut}>
          Sign out
        </button>

        <p className={styles.footnote}>
          Signing out ends your session with the sign-in provider, so a different account can sign
          in.
        </p>
      </div>
    </div>
  );
}
